import { cn } from "@/lib/utils";

export const BRAND = {
  primary: "#155EEF",
  ai: "#7C3AED",
  success: "#12B76A",
  warning: "#F79009",
  critical: "#F04438",
};

export function severityColor(level) {
  const map = {
    Critical: "#F04438", critical: "#F04438", High: "#F04438", high: "#F04438",
    Elevated: "#F79009", Medium: "#F79009", medium: "#F79009", warning: "#F79009",
    "At Risk": "#F79009", "High Risk": "#F04438",
    Moderate: "#155EEF", Low: "#12B76A", low: "#12B76A", Stable: "#12B76A",
    Good: "#12B76A", Excellent: "#12B76A", success: "#12B76A", normal: "#155EEF",
  };
  return map[level] || "#155EEF";
}

export function scoreColor(score) {
  if (score >= 90) return "#12B76A";
  if (score >= 80) return "#155EEF";
  if (score >= 70) return "#F79009";
  return "#F04438";
}

export function ScoreRing({ value, size = 76, label, color }) {
  const stroke = 7;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const col = color || scoreColor(value);
  const off = c - (value / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="hsl(var(--muted))" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={col} strokeWidth={stroke} fill="none"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1)" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold tracking-tight">{value}</span>
        {label && <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>}
      </div>
    </div>
  );
}

export function PriorityBadge({ level, className }) {
  const col = severityColor(level);
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold", className)}
      style={{ color: col, backgroundColor: `${col}1a` }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: col }} />
      {level}
    </span>
  );
}

export function StatCard({ icon: Icon, label, value, sub, accent = "#155EEF", index = 0, onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className="careiq-rise group text-left w-full rounded-xl border bg-card p-4 shadow-sm transition-colors duration-200 hover:border-[color:var(--ring)]"
      style={{ animationDelay: `${index * 60}ms` }}>
      <div className="flex items-center justify-between">
        <span className="overline">{label}</span>
        <span className="flex h-8 w-8 items-center justify-center rounded-md" style={{ backgroundColor: `${accent}1a`, color: accent }}>
          <Icon size={16} strokeWidth={2} />
        </span>
      </div>
      <div className="mt-2 text-2xl font-bold tracking-tight">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </button>
  );
}

export function PageHeader({ overline, title, description, actions }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        {overline && <div className="overline mb-1">{overline}</div>}
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
        {description && <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions}
    </div>
  );
}

export function Avatar({ name, seed = 0, size = 36 }) {
  const initials = (name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  const colors = ["#155EEF", "#7C3AED", "#12B76A", "#F79009", "#0EA5E9", "#DB2777"];
  const bg = colors[seed % colors.length];
  return (
    <span className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
      style={{ width: size, height: size, backgroundColor: bg, fontSize: size * 0.36 }}>
      {initials}
    </span>
  );
}
